import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { ArrowRight, MapPin, Phone, Mail, Calendar, Coffee, Trees, ShieldCheck, Utensils, ParkingCircle, GraduationCap, TentTree, Sparkles } from "lucide-react";
import { useT, useMedia } from "@/lib/i18n";
import heroImg from "@/assets/bosque-hero.jpg";
import ziplineImg from "@/assets/bosque-zipline.jpg";
import ziplineActionImg from "@/assets/bosque-zipline-action.jpg";
import climbingImg from "@/assets/bosque-climbing.jpg";
import archeryImg from "@/assets/bosque-archery.jpg";
import bridgeFamilyImg from "@/assets/bosque-bridge-family.jpg";
import nightImg from "@/assets/bosque-night.jpg";
import forestImg from "@/assets/intro-forest.jpg";

const CDN = "https://www.bosquesuspendido.com/wp-content/uploads";

export const Route = createFileRoute("/about/")({
  component: AboutIndex,
  head: () => ({
    meta: [
      { title: "El Parque — Bosque Suspendido | Centro de Naturaleza La Juliana" },
      { name: "description", content: "Conoce el Centro de Naturaleza La Juliana: pinar centenario en Bollullos de la Mitación, zonas temáticas, áreas de picnic, restaurante y mucho más, a 15 minutos de Sevilla." },
      { property: "og:title", content: "El Parque — Centro de Naturaleza La Juliana" },
      { property: "og:description", content: "Un pulmón verde junto a Sevilla con todo para disfrutar de un día en plena naturaleza." },
      { property: "og:url", content: "https://bosque-suspendido.lovable.app/about" },
      { property: "og:image", content: `${CDN}/2021/09/mapaEntrada-1-1024x600.jpg` },
    ],
    links: [{ rel: "canonical", href: "https://bosque-suspendido.lovable.app/about" }],
  }),
});

const FACILITIES_CN = [
  "Zonas temáticas: dinosaurios, indios y piratas",
  "Estanque didáctico",
  "Talleres por ciclo educativo",
  "Escuela de tiempo libre y animación",
];

const FACILITIES_GENERAL = [
  { i: Coffee, t: "Bar-cafetería y terraza con comida casera" },
  { i: Utensils, t: "Áreas de picnic" },
  { i: ParkingCircle, t: "Aparcamiento para coches y autobuses" },
  { i: GraduationCap, t: "Aula de formación multimedia" },
  { i: ShieldCheck, t: "Aseos públicos" },
];

const GALLERY = [
  { src: ziplineActionImg, alt: "Tirolina entre los pinos del Bosque Suspendido" },
  { src: climbingImg, alt: "Escalada en los árboles" },
  { src: bridgeFamilyImg, alt: "Familias cruzando un puente colgante entre los pinos" },
  { src: archeryImg, alt: "Tiro con arco en el Centro de Naturaleza" },
  { src: forestImg, alt: "Pinar centenario de La Juliana" },
  { src: nightImg, alt: "Bosque nocturno" },
];

function AboutIndex() {
  const { tk } = useT();
  const hero = useMedia("about.hero", heroImg, "Centro de Naturaleza La Juliana");

  return (
    <Layout>
      {/* Hero — full-bleed photo, immersive */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden bg-primary">
        <img src={hero.url} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/30 to-transparent" aria-hidden />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-28 w-full text-center">
          <span className="text-xs tracking-[0.4em] uppercase text-gold-soft font-bold">
            {tk("about.index.kicker", "Bienvenidos a El Parque")}
          </span>
          <h1 className="font-serif font-extrabold text-5xl md:text-7xl mt-5 max-w-4xl mx-auto text-primary-foreground leading-tight drop-shadow-md">
            {tk("about.index.title.a", "Centro de Naturaleza ")}
            <span className="text-gold-soft">{tk("about.index.title.b", "La Juliana")}</span>
          </h1>
          <p className="mt-6 text-primary-foreground/90 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            {tk("about.index.intro", "Un pinar centenario a las puertas de Doñana. Todo lo necesario para un día de aventura y naturaleza, a 15 minutos de Sevilla.")}
          </p>
          <div className="mt-12 inline-block relative">
            <div className="absolute -top-10 -right-14 rotate-12 bg-gold text-primary font-serif font-bold px-4 py-1.5 rounded-md shadow-lg border-2 border-dashed border-primary text-sm">
              {tk("about.index.badge", "¡Desde 2004!")}
            </div>
            <a href="#entorno" className="wood-btn inline-flex items-center gap-2 px-10 py-4 text-sm tracking-[0.25em] uppercase">
              {tk("about.index.cta", "Descubre el parque")} <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* El entorno — split with map */}
      <section id="entorno" className="scroll-mt-24 py-24 border-b border-gold/15">
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row gap-16 items-center">
          <div className="lg:w-1/2 space-y-8">
            <span className="inline-block bg-gold/15 text-primary font-bold px-4 py-1 rounded-full text-xs tracking-[0.25em] uppercase">
              {tk("about.entorno.kicker", "El entorno")}
            </span>
            <h2 className="font-serif font-extrabold text-4xl md:text-5xl leading-tight text-primary">
              {tk("about.entorno.title.a", "Un pinar lleno de ")}
              <span className="text-gold">{tk("about.entorno.title.b", "posibilidades")}</span>
            </h2>
            <p className="text-lg leading-relaxed text-foreground/80">
              {tk("about.intro.p1", "El Parque de La Juliana, zona de paso de aves al Parque Nacional de Doñana, acoge el Centro de Naturaleza, el Bosque Suspendido y el Restaurante La Juliana: múltiples áreas y servicios para disfrutar de un día completo en la naturaleza.")}
            </p>
            <blockquote className="italic text-gold-deep font-semibold">
              «{tk("about.index.quote", "Cuidar el parque es tarea de todos…")}»
            </blockquote>
            <div className="flex gap-4">
              <div className="p-6 bg-card shadow-sm rounded-2xl flex-1 border-l-4 border-gold">
                <span className="block font-serif font-extrabold text-3xl text-primary">15 min</span>
                <span className="text-sm text-muted-foreground font-semibold">{tk("about.stat.sevilla", "de Sevilla")}</span>
              </div>
              <div className="p-6 bg-card shadow-sm rounded-2xl flex-1 border-l-4 border-gold">
                <span className="block font-serif font-extrabold text-3xl text-primary">2004</span>
                <span className="text-sm text-muted-foreground font-semibold">{tk("about.stat.pioneros", "pioneros en Andalucía")}</span>
              </div>
            </div>
          </div>
          <div className="lg:w-1/2 relative">
            <div className="absolute -inset-4 bg-gold rounded-2xl -rotate-2 opacity-15" aria-hidden />
            <div className="relative z-10 rounded-2xl shadow-2xl overflow-hidden border-4 border-card bg-card">
              <img src={`${CDN}/2021/09/mapaEntrada-1-1024x600.jpg`} alt="Mapa de entrada al Centro de Naturaleza La Juliana" className="w-full h-auto" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Video band */}
      <section className="bg-primary py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-serif font-extrabold text-3xl md:text-4xl text-primary-foreground text-center mb-10">
            {tk("about.video.title", "Siente la aventura")}
          </h2>
          <div className="rounded-3xl overflow-hidden border-4 border-gold/30 aspect-video bg-black shadow-2xl">
            <iframe
              src="https://www.youtube.com/embed/jg1ti-sF8R8"
              title="Video tour del Bosque Suspendido en el Centro de Naturaleza de La Juliana"
              className="w-full h-full"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* Facilities — card grid */}
      <section className="py-24 bg-card/40 border-b border-gold/15">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.4em] uppercase text-gold font-bold">{tk("about.facilities.kicker", "Instalaciones del CNJ")}</span>
            <h2 className="font-serif font-extrabold text-4xl md:text-5xl mt-4 text-primary">{tk("about.facilities.title", "Área recreativa")}</h2>
            <p className="mt-5 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {tk("about.facilities.intro", "Instalaciones para escolares, familias, grupos y empresas. Los talleres y zonas temáticas son sólo para grupos concertados.")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-card p-8 rounded-3xl shadow-sm hover:shadow-xl transition-shadow border border-primary/5">
              <div className="w-12 h-12 bg-gold rounded-xl mb-6 flex items-center justify-center text-primary">
                <TentTree size={24} />
              </div>
              <h3 className="font-serif font-bold text-2xl mb-4 text-primary">{tk("about.facilities.cn.title", "Centro de Naturaleza")}</h3>
              <ul className="space-y-3">
                {FACILITIES_CN.map((f) => (
                  <li key={f} className="flex gap-3 text-sm text-foreground/80">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gold shrink-0" aria-hidden />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-card p-8 rounded-3xl shadow-sm hover:shadow-xl transition-shadow border border-primary/5">
              <div className="w-12 h-12 bg-gold rounded-xl mb-6 flex items-center justify-center text-primary">
                <Coffee size={24} />
              </div>
              <h3 className="font-serif font-bold text-2xl mb-4 text-primary">{tk("about.facilities.gen.title", "Servicios generales")}</h3>
              <ul className="space-y-3">
                {FACILITIES_GENERAL.map(({ i: Icon, t }) => (
                  <li key={t} className="flex gap-3 text-sm text-foreground/80">
                    <Icon className="text-gold shrink-0 mt-0.5" size={16} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Link to="/services" className="group relative rounded-3xl overflow-hidden min-h-[18rem] block shadow-sm hover:shadow-xl transition-shadow">
              <img src={ziplineImg} alt="Parque de aventura en los árboles Bosque Suspendido" loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/20 to-transparent" aria-hidden />
              <div className="absolute inset-x-0 bottom-0 p-8">
                <div className="flex items-center gap-2 text-gold-soft text-xs tracking-[0.3em] uppercase font-bold"><Trees size={14} /> {tk("about.facilities.bs.kicker", "Aventura en los árboles")}</div>
                <h3 className="font-serif font-bold text-2xl text-primary-foreground mt-2">{tk("about.facilities.bs.title", "Bosque Suspendido")}</h3>
                <span className="mt-3 inline-flex items-center gap-2 text-primary-foreground/90 text-sm font-semibold">
                  {tk("about.facilities.bs.cta", "Ver actividades")} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="border-b border-gold/15">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {GALLERY.map(({ src, alt }, idx) => (
              <div key={src} className={`overflow-hidden rounded-2xl border border-gold/15 bg-card ${idx === 0 || idx === 4 ? "md:row-span-2 md:aspect-auto aspect-[4/3]" : "aspect-[4/3]"}`}>
                <img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bar / Restaurant — featured dark panel */}
      <section id="bar" className="scroll-mt-32 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-primary rounded-[3rem] overflow-hidden flex flex-col md:flex-row shadow-2xl">
            <div className="md:w-1/2 grid grid-cols-2">
              {[
                `${CDN}/2021/11/01813445-53e8-4da7-b780-1ee90bd3da58.jpg`,
                `${CDN}/2021/08/dccdc089-bbea-46fc-a778-3ac7179ced9e-e1629292650926.jpg`,
                `${CDN}/2021/12/IMG-6471-scaled.jpg`,
                `${CDN}/2021/12/de0d0153-9855-41ff-bc20-e6033e6cb36b.jpg`,
              ].map((src) => (
                <div key={src} className="aspect-square overflow-hidden">
                  <img src={src} alt="Bar Terraza La Juliana" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </div>
            <div className="md:w-1/2 p-10 lg:p-16 flex flex-col justify-center">
              <span className="text-xs tracking-[0.4em] uppercase text-gold-soft font-bold">{tk("about.bar.kicker", "Bar-Terraza La Juliana")}</span>
              <h2 className="font-serif font-extrabold text-3xl md:text-4xl text-primary-foreground mt-4 mb-6">{tk("about.bar.title", "Comer y descansar entre pinos")}</h2>
              <p className="text-primary-foreground/85 leading-relaxed">
                {tk("about.bar.p1", "Un encantador edificio de madera para desayunar, comer y merendar rodeados de naturaleza, con terraza al aire libre y salones con vistas al pinar centenario. Celebraciones, comidas de empresa, comuniones y eventos, al aire libre o en el interior.")}
              </p>
              <div className="mt-8 space-y-3 text-sm">
                <div className="flex items-center gap-3 text-primary-foreground/90"><Phone className="text-gold-soft" size={16} /><a href="tel:+34673484550" className="hover:text-gold-soft font-semibold">+34 673 48 45 50</a></div>
                <div className="flex items-center gap-3 text-primary-foreground/90"><Mail className="text-gold-soft" size={16} /><a href="mailto:marina@grupobosquesuspendido.com" className="hover:text-gold-soft break-all">marina@grupobosquesuspendido.com</a></div>
              </div>
              <Link to="/contact" className="mt-10 self-start wood-btn inline-flex items-center gap-2 px-8 py-3.5 text-xs tracking-[0.3em] uppercase">
                {tk("about.bar.cta", "Consultar menús")} <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* History — condensed band over forest photo */}
      <section className="relative py-28 bg-primary overflow-hidden">
        <img src={forestImg} alt="" aria-hidden loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <div className="inline-block bg-gold text-primary font-serif font-extrabold px-8 py-3 rounded-md shadow-2xl -rotate-2 mb-10 text-xl md:text-2xl">
            {tk("about.history.title", "PIONEROS DESDE 2004")}
          </div>
          <p className="text-xl md:text-2xl text-primary-foreground leading-relaxed font-light italic">
            «{tk("about.history.p1", "Fuimos el primer parque de aventura en los árboles de Andalucía. Dos décadas de seguridad, diversión y buen trato para familias, colegios, empresas y grupos.")}»
          </p>
          <p className="mt-6 text-sm text-primary-foreground/70 leading-relaxed flex items-center justify-center gap-2">
            <Sparkles size={14} className="text-gold-soft shrink-0" />
            {tk("about.history.p3", "El Bosque Suspendido y el Centro de Naturaleza forman parte del Grupo Bosque Suspendido, especialista en instalaciones de ocio.")}
          </p>
        </div>
      </section>

      {/* Location quick cards */}
      <section className="bg-card/40">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-6">
          <div className="rounded-3xl border-b-8 border-primary bg-card shadow-md p-8 hover:-translate-y-1 transition-transform">
            <MapPin className="text-gold" size={26} />
            <h3 className="mt-4 font-serif font-bold text-xl text-primary">{tk("about.loc.where", "Dónde estamos")}</h3>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Centro de Naturaleza La Juliana<br/>Ctra. A-474 Bollullos de la Mitación — Aznalcázar, Km 15,5</p>
          </div>
          <div className="rounded-3xl border-b-8 border-gold bg-card shadow-md p-8 hover:-translate-y-1 transition-transform">
            <Calendar className="text-gold" size={26} />
            <h3 className="mt-4 font-serif font-bold text-xl text-primary">{tk("about.loc.when", "Horario")}</h3>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Sábados, domingos y festivos de 10:30 a 15:00. Octubre, abril y mayo: 10:30 – 18:00. Julio y agosto solo grupos concertados.</p>
          </div>
          <div className="rounded-3xl border-b-8 border-primary bg-card shadow-md p-8 hover:-translate-y-1 transition-transform">
            <Phone className="text-gold" size={26} />
            <h3 className="mt-4 font-serif font-bold text-xl text-primary">{tk("about.loc.contact", "Contacto")}</h3>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              <a href="tel:+34955778035" className="hover:text-gold block">+34 955 778 035</a>
              <a href="mailto:info@bosquesuspendido.com" className="hover:text-gold block">info@bosquesuspendido.com</a>
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
